/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Row, Col, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import DrawerWrapper, { DrawerFooter } from '../../DrawerWrapper';
import FormSelectField from '../../formFields/FormSelectField';
import FormTextField from '../../formFields/FormTextField';
import FormTextAreaField from '../../formFields/FormTextAreaField';
import FormDateTimeField from '../../formFields/FormDateTimeField';
import FormGeoLocationField from '../../formFields/FormGeoLocationField';
import { ResourceInIncident } from '../../hooks/resourceInIncidentHooks';
import { useResourceSites } from '../../hooks/resourceSiteHooks';
import { useCurrentUserApprovalAuthority } from '../../../../hooks/incident/useCurrentUserApprovalAuthority';
import ApprovalAuthorityWarning from '../../../DesignSystems/ApprovalAuthorityWarning';
import { useIncidentRoster } from '../../hooks/incidentRoster/useIncidentRoster';

interface CheckOutDrawerProps {
  resource: ResourceInIncident;
  onClose: () => void;
  onSave: (data: Partial<ResourceInIncident>) => void;
  isUpdate?: boolean;
}

interface CheckoffSignature {
  checked: boolean;
  name?: string;
  position?: string;
  remarks?: string;
  user_guid?: string;
  datetime?: string | null;
}

interface CustomSignature extends CheckoffSignature {
  id: string;
}

export interface CheckOutFormData {
  useCustomDestination?: boolean;
  destinationSiteId?: string;
  destinationGeolocation?: {
    lat?: number;
    lng?: number;
    name?: string;
    address?: string;
  } | null;
  checkoutEtaHome?: string;
  checkoutTravelMethod: string;
  checkoutContactInfo: string;
  checkoutRemarks?: string;
  checkoutCheckoffs: Record<string, CheckoffSignature>;
  // New ICS 221 fields
  plannedReleaseDatetime?: string;
  roomOvernight?: string;
  estimatedDepartureDatetime?: string;
  actualReleaseDatetime?: string;
  estimatedArrivalDatetime?: string;
  manifestRequired?: string;
  manifestNumber?: string;
  unitAgencyNotified?: string;
  unitAgencyNotifiedBool?: string;
  incidentLodgingInfo?: string;
}

const checkoffUnits = [{ id: 'demobilization', label: 'Demobilization Unit' }];

const CheckOutDrawer: React.FC<CheckOutDrawerProps> = ({
  resource,
  onClose,
  onSave,
  isUpdate = false,
}) => {
  const { data: resourceSites = [] } = useResourceSites();
  const currentUser = useSelector(
    (state: { app: { user: { user_guid: string; name?: string } } }) =>
      state.app.user
  );
  const { data: rosterData } = useIncidentRoster();
  const currentMember = rosterData?.find(
    (member) => member.userId === currentUser?.user_guid
  );
  const hasApprovalAuthority = useCurrentUserApprovalAuthority();

  // Initialize custom signatures from resource data
  const initialCustomSignatures: CustomSignature[] =
    isUpdate && resource.checkout_checkoffs
      ? Object.entries(resource.checkout_checkoffs)
          .filter(([key]) => !checkoffUnits.find((unit) => unit.id === key))
          .map(([key, value]: [string, CheckoffSignature]) => ({
            id: key,
            checked: value.checked || false,
            name: value.name || '',
            position: value.position || '',
            remarks: value.remarks || '',
            user_guid: value.user_guid || '',
            datetime: value.datetime || null,
          }))
      : [];

  const [customSignatures, setCustomSignatures] = useState<CustomSignature[]>(
    initialCustomSignatures
  );

  const [checkoffs, setCheckoffs] = useState<Record<string, CheckoffSignature>>(
    isUpdate && resource.checkout_checkoffs
      ? Object.entries(resource.checkout_checkoffs).reduce(
          (acc, [key, value]: [string, CheckoffSignature]) => {
            // Check if this is a custom signature (not in predefined units)
            if (!checkoffUnits.find((unit) => unit.id === key)) {
              // Skip custom signatures, they're handled separately
              return acc;
            }
            return {
              ...acc,
              [key]: {
                checked: value.checked || false,
                name: value.name || '',
                position: value.position || '',
                remarks: value.remarks || '',
                user_guid: value.user_guid || '',
                datetime: value.datetime || null,
              },
            };
          },
          {}
        )
      : checkoffUnits.reduce(
          (acc, unit) => ({
            ...acc,
            [unit.id]: {
              checked: false,
              name: '',
              position: '',
              remarks: '',
              user_guid: '',
              datetime: null,
            },
          }),
          {}
        )
  );

  const form = useForm<CheckOutFormData>({
    mode: 'onChange',
    defaultValues: {
      useCustomDestination:
        !!resource.destination_geolocation && !resource.destination_site_id,
      destinationSiteId: resource.destination_site_id || '',
      destinationGeolocation: resource.destination_geolocation || null,
      checkoutEtaHome: resource.checkout_eta_home || '',
      checkoutTravelMethod: resource.checkout_travel_method || '',
      checkoutContactInfo: resource.checkout_contact_info || '',
      checkoutRemarks: resource.checkout_remarks || '',
      plannedReleaseDatetime: resource.planned_release_datetime || '',
      roomOvernight: resource.room_overnight ? 'yes' : 'no',
      estimatedDepartureDatetime: resource.estimated_departure_datetime || '',
      actualReleaseDatetime: resource.actual_release_datetime || '',
      estimatedArrivalDatetime: resource.estimated_arrival_datetime || '',
      manifestRequired: resource.manifest_required ? 'yes' : 'no',
      manifestNumber: resource.manifest_number || '',
      unitAgencyNotified: resource.unit_agency_notified || '',
      unitAgencyNotifiedBool: resource.unit_agency_notified_bool ? 'yes' : 'no',
      incidentLodgingInfo: resource.incident_lodging_info || '',
    },
  });

  const { handleSubmit, watch, reset } = form;
  const useCustomDestination = watch('useCustomDestination');
  const manifestRequired = watch('manifestRequired');
  const unitAgencyNotifiedBool = watch('unitAgencyNotifiedBool');

  // Reset form when resource changes (for update mode)
  useEffect(() => {
    if (isUpdate && resource) {
      // Determine if using custom destination
      let usingCustom = false;
      let destinationSiteId = '';

      // First priority: Check if we have a destination_site_id
      if (resource.destination_site_id) {
        usingCustom = false;
        destinationSiteId = resource.destination_site_id;
      }
      // Second priority: Check if we have a geolocation
      else if (resource.destination_geolocation) {
        usingCustom = true;
      }
      // Third priority: Try to match checkout_destination to a site
      else if (resource.checkout_destination && resourceSites.length > 0) {
        const matchingSite = resourceSites.find(
          (site) => site.name === resource.checkout_destination
        );
        if (matchingSite) {
          usingCustom = false;
          destinationSiteId = matchingSite.id;
        } else {
          // If we have a checkout_destination but no matching site, treat as custom
          usingCustom = true;
        }
      }

      reset({
        useCustomDestination: usingCustom,
        destinationSiteId: destinationSiteId,
        destinationGeolocation:
          resource.destination_geolocation ||
          (usingCustom && resource.checkout_destination
            ? {
                name: resource.checkout_destination,
              }
            : null),
        checkoutEtaHome: resource.checkout_eta_home || '',
        checkoutTravelMethod: resource.checkout_travel_method || '',
        checkoutContactInfo: resource.checkout_contact_info || '',
        checkoutRemarks: resource.checkout_remarks || '',
        plannedReleaseDatetime: resource.planned_release_datetime || '',
        roomOvernight: resource.room_overnight ? 'yes' : 'no',
        estimatedDepartureDatetime: resource.estimated_departure_datetime || '',
        actualReleaseDatetime: resource.actual_release_datetime || '',
        estimatedArrivalDatetime: resource.estimated_arrival_datetime || '',
        manifestRequired: resource.manifest_required ? 'yes' : 'no',
        manifestNumber: resource.manifest_number || '',
        unitAgencyNotified: resource.unit_agency_notified || '',
        unitAgencyNotifiedBool: resource.unit_agency_notified_bool
          ? 'yes'
          : 'no',
        incidentLodgingInfo: resource.incident_lodging_info || '',
      });

      // Reset checkoffs and custom signatures
      if (resource.checkout_checkoffs) {
        const standardCheckoffs: Record<string, CheckoffSignature> = {};
        const customSigs: Array<CustomSignature> = [];

        Object.entries(resource.checkout_checkoffs).forEach(
          ([key, value]: [
            string,
            {
              checked?: boolean;
              name?: string;
              signature?: string;
              position?: string;
              remarks?: string;
              user_guid?: string;
              datetime?: string | null;
            },
          ]) => {
            if (!checkoffUnits.find((unit) => unit.id === key)) {
              // Custom signature
              customSigs.push({
                id: key,
                checked: value.checked || false,
                name: value.name || '',
                position: value.position || '',
                remarks: value.remarks || '',
                user_guid: value.user_guid || '',
                datetime: value.datetime || null,
              });
            } else {
              // Standard checkoff
              standardCheckoffs[key] = {
                checked: value.checked || false,
                name: value.name || '',
                position: value.position || '',
                remarks: value.remarks || '',
                user_guid: value.user_guid || '',
                datetime: value.datetime || null,
              };
            }
          }
        );

        setCheckoffs(standardCheckoffs);
        setCustomSignatures(customSigs);
      } else {
        // Reset to empty state
        setCheckoffs(
          checkoffUnits.reduce(
            (acc, unit) => ({
              ...acc,
              [unit.id]: {
                checked: false,
                name: '',
                position: '',
                remarks: '',
                user_guid: '',
                datetime: null,
              },
            }),
            {}
          )
        );
        setCustomSignatures([]);
      }
    }
  }, [isUpdate, resource, reset, resourceSites]);

  const resourceSiteOptions = useMemo(
    () =>
      resourceSites.map((site) => ({
        value: site.id,
        label: site.name,
      })),
    [resourceSites]
  );

  const handleCheckoffChange = (
    unitId: string,
    field: 'checked' | 'remarks' | 'position',
    value: boolean | string,
    isCustom: boolean = false
  ) => {
    if (isCustom) {
      setCustomSignatures((prev) => {
        return prev.map((sig) => {
          if (sig.id !== unitId) return sig;

          // If checking the box, auto-fill name but allow position to be edited
          if (field === 'checked' && value === true && currentMember) {
            return {
              ...sig,
              checked: true,
              name: currentMember.userName || currentUser.name || '',
              user_guid: currentUser.user_guid,
              datetime: !sig.datetime ? new Date().toISOString() : sig.datetime,
            };
          }

          // If unchecking, only allow if current user checked it
          if (field === 'checked' && value === false) {
            if (sig.user_guid && sig.user_guid !== currentUser.user_guid) {
              return sig; // Don't allow unchecking
            }
            return {
              id: sig.id,
              checked: false,
              name: '',
              position: '',
              remarks: '',
              user_guid: '',
              datetime: null,
            };
          }

          // Handle other field updates
          return {
            ...sig,
            [field]: value,
          };
        });
      });
    } else {
      setCheckoffs((prev) => {
        const current = prev[unitId];

        // If checking the box, auto-fill name and position
        if (field === 'checked' && value === true && currentMember) {
          // Only set new timestamp if this is the first time being checked
          const isFirstTimeChecking = !current.checked || !current.datetime;

          return {
            ...prev,
            [unitId]: {
              ...current,
              checked: true,
              name: currentMember.userName || currentUser.name || '',
              position: currentMember.incidentPositions
                ? currentMember.incidentPositions
                    .map((pos) => pos.positionName)
                    .join(', ')
                : '',
              user_guid: currentUser.user_guid,
              datetime: isFirstTimeChecking
                ? new Date().toISOString()
                : current.datetime,
            },
          };
        }

        // If unchecking, only allow if current user checked it
        if (field === 'checked' && value === false) {
          if (
            current.user_guid &&
            current.user_guid !== currentUser.user_guid
          ) {
            return prev; // Don't allow unchecking
          }
          return {
            ...prev,
            [unitId]: {
              checked: false,
              name: '',
              position: '',
              remarks: '',
              user_guid: '',
              datetime: null,
            },
          };
        }

        // Handle remarks update
        if (field === 'remarks') {
          return {
            ...prev,
            [unitId]: {
              ...current,
              remarks: value as string,
            },
          };
        }

        return prev;
      });
    }
  };

  const addCustomSignature = () => {
    const newId = `custom_${Date.now()}`;
    setCustomSignatures((prev) => [
      ...prev,
      {
        id: newId,
        checked: false,
        name: '',
        position: '',
        remarks: '',
        user_guid: '',
        datetime: null,
      },
    ]);
  };

  const removeCustomSignature = (id: string) => {
    setCustomSignatures((prev) => prev.filter((sig) => sig.id !== id));
  };

  const onSubmit = (data: CheckOutFormData) => {
    // Combine all checkoffs (standard + custom)
    const allCheckoffs = { ...checkoffs };
    customSignatures.forEach((sig) => {
      allCheckoffs[sig.id] = {
        checked: sig.checked,
        name: sig.name,
        position: sig.position,
        remarks: sig.remarks,
        user_guid: sig.user_guid,
        datetime: sig.datetime,
      };
    });

    const checkoffsWithDateTime = Object.entries(allCheckoffs).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: {
          ...value,
          // Preserve existing datetime, don't create new one
          datetime: value.datetime || null,
        },
      }),
      {}
    );

    const checkoutData: Partial<ResourceInIncident> = {
      release_method: 'demobilized', // Always set to demobilized
      checkout_eta_home: data.checkoutEtaHome || undefined,
      checkout_travel_method: data.checkoutTravelMethod,
      checkout_contact_info: data.checkoutContactInfo,
      checkout_remarks: data.checkoutRemarks || undefined,
      checkout_checkoffs: checkoffsWithDateTime,
      planned_release_datetime: data.plannedReleaseDatetime || undefined,
      room_overnight: data.roomOvernight === 'yes' || undefined,
      estimated_departure_datetime:
        data.estimatedDepartureDatetime || undefined,
      actual_release_datetime: data.actualReleaseDatetime || undefined,
      estimated_arrival_datetime: data.estimatedArrivalDatetime || undefined,
      manifest_required: data.manifestRequired === 'yes' || undefined,
      manifest_number:
        data.manifestRequired === 'yes' ? data.manifestNumber : undefined,
      unit_agency_notified:
        data.unitAgencyNotifiedBool === 'yes'
          ? data.unitAgencyNotified
          : undefined,
      unit_agency_notified_bool:
        data.unitAgencyNotifiedBool === 'yes' || undefined,
      incident_lodging_info: data.incidentLodgingInfo || undefined,
    };

    // Only set checkout_datetime for new check-outs, not updates
    if (!isUpdate) {
      checkoutData.checkout_datetime = new Date().toISOString();
    }

    // Handle destination based on toggle
    if (data.useCustomDestination) {
      if (data.destinationGeolocation) {
        checkoutData.destination_geolocation = data.destinationGeolocation;
        checkoutData.checkout_destination =
          data.destinationGeolocation.name ||
          data.destinationGeolocation.address ||
          'Custom Destination';
      } else {
        // If custom is selected but no geolocation, use checkout_destination as custom text
        checkoutData.checkout_destination = 'Custom Destination';
      }
      // Clear site ID when using custom location
      checkoutData.destination_site_id = undefined;
    } else if (data.destinationSiteId) {
      checkoutData.destination_site_id = data.destinationSiteId;
      // Clear geolocation when using site
      checkoutData.destination_geolocation = undefined;
      // Set checkout_destination to the selected site's name
      const selectedSite = resourceSites.find(
        (site) => site.id === data.destinationSiteId
      );
      if (selectedSite) {
        checkoutData.checkout_destination = selectedSite.name;
      }
    }

    onSave(checkoutData);
  };

  const demobCheckoffComplete =
    checkoffs.demobilization?.checked && checkoffs.demobilization?.name;

  // Build resource personnel released string
  const resourcePersonnelReleased =
    `${resource.resource_name || resource.resource_name_identifier || 'Unknown'} - ${
      resource.resource_kind || resource.resource_set_kind || ''
    }`.trim();

  return (
    <DrawerWrapper
      toggle={onClose}
      title={
        isUpdate ? 'Update ICS 221 Demobilization' : 'ICS 221 Demobilization'
      }
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-4">
            {isUpdate && resource.checkout_datetime && (
              <Row className="mb-4">
                <Col md={12}>
                  <div className="form-group">
                    <label>Initial Demobilization Date/Time</label>
                    <input
                      type="text"
                      className="form-control"
                      value={new Date(
                        resource.checkout_datetime
                      ).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      disabled
                      readOnly
                    />
                  </div>
                </Col>
              </Row>
            )}

            <h5 className="mb-3">Release Information</h5>

            {/* Resource/Personnel Released */}
            <Row className="mb-3">
              <Col md={12}>
                <label className="form-label">
                  Resource/Personnel Released
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={resourcePersonnelReleased}
                  disabled
                  readOnly
                />
              </Col>
            </Row>

            {/* Planned Release Date and Time */}
            <Row className="mb-3">
              <Col md={12}>
                <FormDateTimeField
                  fieldName="plannedReleaseDatetime"
                  title="Planned Release Date/Time"
                />
              </Col>
            </Row>

            {/* Order Request Number */}
            <Row className="mb-3">
              <Col md={12}>
                <label className="form-label">Order Request Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={resource.order_request_number || 'N/A'}
                  disabled
                  readOnly
                />
              </Col>
            </Row>

            {/* Remarks */}
            <Row className="mb-3">
              <Col md={12}>
                <FormTextAreaField
                  fieldName="checkoutRemarks"
                  title="Remarks"
                />
              </Col>
            </Row>

            {/* Estimated Departure */}
            <Row className="mb-3">
              <Col md={12}>
                <FormDateTimeField
                  fieldName="estimatedDepartureDatetime"
                  title="Estimated Departure Date/Time"
                />
              </Col>
            </Row>

            {/* Destination */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Check
                  type="switch"
                  id="use-custom-destination"
                  label="Use Custom Demobilization Destination"
                  {...form.register('useCustomDestination')}
                  className="mb-3"
                />

                {useCustomDestination ? (
                  <FormGeoLocationField
                    name="destinationGeolocation"
                    label="Custom Demobilization Destination"
                  />
                ) : (
                  <>
                    {resourceSites.length === 0 ? (
                      <div className="text-muted">
                        Loading destination sites...
                      </div>
                    ) : (
                      <FormSelectField
                        fieldName="destinationSiteId"
                        title="Demobilization Destination Site"
                        options={resourceSiteOptions}
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>

            {/* Travel Method */}
            <Row className="mb-3">
              <Col md={12}>
                <FormTextField
                  fieldName="checkoutTravelMethod"
                  title="Travel Method"
                />
              </Col>
            </Row>

            {/* Manifest */}
            <Row className="mb-3">
              <Col md={6}>
                <label className="form-label">Manifest</label>
                <div>
                  <Form.Check
                    type="radio"
                    id="manifest-yes"
                    label="Yes"
                    value="yes"
                    {...form.register('manifestRequired')}
                    inline
                  />
                  <Form.Check
                    type="radio"
                    id="manifest-no"
                    label="No"
                    value="no"
                    {...form.register('manifestRequired')}
                    inline
                  />
                </div>
              </Col>
              {manifestRequired === 'yes' && (
                <Col md={6}>
                  <FormTextField
                    fieldName="manifestNumber"
                    title="Manifest #"
                  />
                </Col>
              )}
            </Row>

            {/* Room Overnight */}
            <Row className="mb-3">
              <Col md={12}>
                <label className="form-label">Room Overnight</label>
                <div>
                  <Form.Check
                    type="radio"
                    id="room-overnight-yes"
                    label="Yes"
                    value="yes"
                    {...form.register('roomOvernight')}
                    inline
                  />
                  <Form.Check
                    type="radio"
                    id="room-overnight-no"
                    label="No"
                    value="no"
                    {...form.register('roomOvernight')}
                    inline
                  />
                </div>
              </Col>
            </Row>

            {/* Actual Release Date and Time */}
            <Row className="mb-3">
              <Col md={12}>
                <FormDateTimeField
                  fieldName="actualReleaseDatetime"
                  title="Actual Release Date/Time"
                />
              </Col>
            </Row>

            {/* Estimated Arrival Date and Time */}
            <Row className="mb-3">
              <Col md={12}>
                <FormDateTimeField
                  fieldName="estimatedArrivalDatetime"
                  title="Estimated Arrival Date/Time"
                />
              </Col>
            </Row>

            {/* Contact Information While Traveling */}
            <Row className="mb-3">
              <Col md={12}>
                <FormTextField
                  fieldName="checkoutContactInfo"
                  title="Contact Information While Traveling"
                />
              </Col>
            </Row>

            {/* Incident Lodging Info */}
            <Row className="mb-3">
              <Col md={12}>
                <FormTextAreaField
                  fieldName="incidentLodgingInfo"
                  title="Incident Lodging Information"
                />
              </Col>
            </Row>

            {/* ETA Home */}
            <Row className="mb-3">
              <Col md={12}>
                <FormTextField
                  fieldName="checkoutEtaHome"
                  title="ETA to Home (hrs)"
                />
              </Col>
            </Row>

            {/* Unit/Agency Notified */}
            <Row className="mb-3">
              <Col md={6}>
                <label className="form-label">Unit/Agency Notified</label>
                <div>
                  <Form.Check
                    type="radio"
                    id="unit-agency-yes"
                    label="Yes"
                    value="yes"
                    {...form.register('unitAgencyNotifiedBool')}
                    inline
                  />
                  <Form.Check
                    type="radio"
                    id="unit-agency-no"
                    label="No"
                    value="no"
                    {...form.register('unitAgencyNotifiedBool')}
                    inline
                  />
                </div>
              </Col>
              {unitAgencyNotifiedBool === 'yes' && (
                <Col md={6}>
                  <FormTextField
                    fieldName="unitAgencyNotified"
                    title="Unit/Agency Name"
                  />
                </Col>
              )}
            </Row>

            {checkoffUnits.map((unit) => {
              const checkoff = checkoffs[unit.id];
              const canUncheck =
                !checkoff?.user_guid ||
                checkoff.user_guid === currentUser?.user_guid;
              return (
                <React.Fragment key={unit.id}>
                  <div className="mb-3 p-3 border rounded">
                    <Row className="mb-2">
                      <Col md={12}>
                        <Form.Check
                          type="checkbox"
                          id={`checkoff-${unit.id}`}
                          label={<strong>{unit.label}</strong>}
                          checked={checkoff?.checked || false}
                          onChange={(e) =>
                            handleCheckoffChange(
                              unit.id,
                              'checked',
                              e.target.checked
                            )
                          }
                          disabled={
                            (checkoff?.checked && !canUncheck) ||
                            !hasApprovalAuthority
                          }
                        />
                        {checkoff?.checked && !canUncheck && (
                          <small className="text-muted">
                            (Signed by another user - cannot modify)
                          </small>
                        )}
                        {!hasApprovalAuthority && (
                          <ApprovalAuthorityWarning
                            message={`You don't have permission to approve ${unit.label.toLowerCase()} sign-offs. Please seek permission from a user with approval authority in the incident roster.`}
                            variant="warning"
                            className="mt-2"
                            inline
                          />
                        )}
                      </Col>
                    </Row>

                    {checkoff?.checked && (
                      <>
                        <Row className="mb-2">
                          <Col md={6}>
                            <label className="form-label small">Name</label>
                            <Form.Control
                              type="text"
                              value={checkoff.name || ''}
                              disabled
                              size="sm"
                            />
                          </Col>
                          <Col md={6}>
                            <label className="form-label small">Position</label>
                            <Form.Control
                              type="text"
                              value={checkoff.position || ''}
                              disabled
                              size="sm"
                            />
                          </Col>
                        </Row>
                        {checkoff.datetime && (
                          <Row className="mb-2">
                            <Col md={12}>
                              <small className="text-muted">
                                Signed on:{' '}
                                {new Date(checkoff.datetime).toLocaleString(
                                  'en-US',
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  }
                                )}
                              </small>
                            </Col>
                          </Row>
                        )}
                        <Row>
                          <Col md={12}>
                            <label className="form-label small">Remarks</label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              placeholder="Optional remarks..."
                              value={checkoff.remarks || ''}
                              onChange={(e) =>
                                handleCheckoffChange(
                                  unit.id,
                                  'remarks',
                                  e.target.value
                                )
                              }
                              disabled={!canUncheck || !hasApprovalAuthority}
                              size="sm"
                            />
                          </Col>
                        </Row>
                      </>
                    )}

                    {unit.id === 'demobilization' && (
                      <div className="mt-2">
                        <small className="text-danger">
                          * Required for final checkout
                        </small>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {!demobCheckoffComplete && (
              <div className="alert alert-warning">
                <small>
                  <strong>Important:</strong> The Demobilization Unit must sign
                  off before the resource can be fully checked out. Without this
                  signature, the resource will be marked as &quot;In
                  Process&quot; and will need to be updated later.
                </small>
              </div>
            )}
          </div>

          <DrawerFooter>
            <StylishNewButton
              type="button"
              className="button button--link me-2"
              onClick={onClose}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton type="submit" className="button button--primary">
              {isUpdate ? 'Update Demobilization' : 'Demobilize Resource'}
            </StylishNewButton>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default CheckOutDrawer;
